/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';

import { Grid } from '@material-ui/core';

import { useApiError, useAppData, useLoading } from '../../providers';

import { getFeatureFlagIsEnabled, getIBPartnerHierarchyModel } from './PartnersHierarchyService';

import { useStyles } from './styles';

import { FilterButton, TreeHierarchy } from './components';

import { PartnerHierarchicalViewModel, Partnership } from './PartnersHierarchyModels';

import { Header, PageTitle, SearchField } from '../../components';

import { useCommonStyles } from '../../styles';

function escapeRegExp(value: string): string {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function isFeatureFlagEnabled(response: any): boolean {
  const value = response?.data;

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  if (typeof value === 'object' && value !== null) {
    if (typeof value.isEnabled === 'boolean') {
      return value.isEnabled;
    }

    if (typeof value.enabled === 'boolean') {
      return value.enabled;
    }

    if (typeof value.value === 'boolean') {
      return value.value;
    }
  }

  return false;
}

export const PartnersHierarchy: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const { userData } = useAppData();
  const { setIsLoading } = useLoading();
  const { addError } = useApiError();
  const { translations } = useAppData();

  const [translation, setTranslation] = useState<any>([]);

  const [hierarchy, setHierarchy] = useState<PartnerHierarchicalViewModel>();

  const [filteredHierarchy, setFilteredHierarchy] = useState<PartnerHierarchicalViewModel>();

  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>(['prime', 'master', 'ib', 'subIb', 'mlm', 'terminated']);

  const [isDashboardEnabled, setIsDashboardEnabled] = useState<boolean>(false);

  const getPartnerHierarchy = useCallback(
    (accountGuid: string) => {
      setIsLoading(true);

      getIBPartnerHierarchyModel(accountGuid)
        .then((response: any) => {
          setHierarchy(response.data);

          setFilteredHierarchy(response.data);

          setIsLoading(false);
        })
        .catch((error) => {
          addError(JSON.stringify(error.response));
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [setHierarchy],
  );

  useEffect(() => {
    try {
      setTranslation(JSON.parse(translations));
    } catch {
      setTranslation({});
    }
  }, [translations]);

  useEffect(() => {
    getFeatureFlagIsEnabled('mib-downline-dashboard-access')
      .then((response: any) => {
        setIsDashboardEnabled(isFeatureFlagEnabled(response));
      })
      .catch(() => {
        setIsDashboardEnabled(false);
      });
  }, []);

  useEffect(() => {
    const userLoginData = JSON.parse(userData);

    const account = userLoginData.accounts[0];

    getPartnerHierarchy(account.accountGuid);
  }, [getPartnerHierarchy]);

  const requestSearch = (searchValue: string) => {
    if (searchValue !== '') {
      const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');

      const filteredRows: Partnership[] = [];

      if (hierarchy) {
        hierarchy.partnerships.forEach((row) => {
          if (searchRegex.test(row.partnerName.toString()) || searchRegex.test(row.partnershipCode.toString())) {
            filteredRows.push(row);
          }
        });

        setFilteredHierarchy({
          name: hierarchy.name,
          clientsCount: hierarchy.clientsCount,
          accountsCount: hierarchy.accountsCount,
          partnerships: filteredRows,
        });
      }
    } else {
      setFilteredHierarchy(hierarchy);
    }
  };

  return (
    <>
      <Header title={translation.text_6386} />

      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6386} />

        <Grid container direction="column" className={commonClasses.mainContainer}>
          <Grid container direction="row" spacing={1} alignContent="flex-start" className={classes.pageTitle} justify="space-between">
            <SearchField onSearch={requestSearch} showReset placeholder={translation.hierarchySearchPlaceholder} />

            <FilterButton translation={translation} selectedCheckboxes={selectedCheckboxes} onChange={(selected) => setSelectedCheckboxes(selected)} />
          </Grid>

          <Grid item>{filteredHierarchy && <TreeHierarchy translation={translation} data={filteredHierarchy} selectedCheckboxes={selectedCheckboxes} isDashboardEnabled={isDashboardEnabled} />}</Grid>
        </Grid>
      </Grid>
    </>
  );
};
